/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import styles from './NPI.module.scss';
import NPIRadioButtons from './NPIRadioButtons';
import { clearFetchNpiResults, fetchNPIList, remindNpiLater, selectNPIState, validateNPINumber } from './npiSlice';
import NPIForm from './NPIForm';
import { analyticsTrack } from '../sharedcomponents/analytics_utils';
import { selectPearlUserInfo } from '../sharedcomponents/Login/userSlice';

export interface NPIProps {
  closeNpiPrompt: (show: boolean) => void;
  prompt_id: string | undefined;
  show: boolean;
}

const NPI: React.FC<NPIProps> = ({ closeNpiPrompt, prompt_id, show }) => {
  const [showForm, setShowForm] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [npiNumber, setNpiNumber] = useState<string>('');
  const [hasNpi, setHasNpi] = useState<boolean>(true);
  const npiUserList = useAppSelector(selectNPIState);
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  const handleSubmit = async (id: string, type?: string) => {
    const data = await dispatch(validateNPINumber({ id, type }));
    if (data.payload) {
      closeNpiPrompt(false);
    }
  };

  const handleGoBack = () => {
    setShowForm(false);
    dispatch(clearFetchNpiResults());
  };

  const handleCloseNpi = () => {
    void dispatch(remindNpiLater({ prompt_id }));
    closeNpiPrompt(false);
  };
  // Handle input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Use regex to allow only digits and restrict to 10 characters
    if (/^\d{0,10}$/.test(inputValue)) {
      setNpiNumber(inputValue);
    }
  };

  function handleFetchNpiViaProfession() {
    analyticsTrack('npi_skipped_profession_submitted', {}, pearlUserInfo);
    setHasNpi(false);
  }

  return (
    <Dialog
      open={show}
      onClose={(event: React.SyntheticEvent, reason: string) => {
        if (reason === 'backdropClick') {
          return;
        }
      }}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '2px',
          maxWidth: '418px',
        },
      }}
      disableEscapeKeyDown
    >
      <div className={styles.npiDialogContainer} data-testid="npi-dialog">
        <IconButton
          aria-label="close"
          onClick={handleCloseNpi}
          className={styles.npiCancelButton}
          data-testid="npi-close-button"
        >
          <CloseIcon />
        </IconButton>
        {hasNpi ? (
          <>
            {!showForm && (
              <>
                <Typography className={styles.npiDialogTitle}>
                  Please provide your NPI so that we can further customize our recommendations for you.
                </Typography>
                <Typography className={styles.npiDescription}>
                  An NPI is a unique 10-digit identification number issued to health care providers in the United
                  States.
                </Typography>
                <div className={styles.npiInputContainer}>
                  <input
                    className={styles.npiInput}
                    data-testid="npi-input"
                    maxLength={10}
                    value={npiNumber}
                    onChange={handleChange}
                  ></input>
                  <Button
                    onClick={() => handleSubmit(npiNumber)}
                    variant="contained"
                    className={styles.npiSubmitButton}
                    disabled={npiNumber.length < 10}
                    data-testid="npi-submit-button"
                  >
                    Submit
                  </Button>
                </div>
                <Button
                  variant="text"
                  className={styles.findNpiButton}
                  onClick={() => setShowForm(true)}
                  data-testid="find-npi-button"
                >
                  Forgot your NPI? We'll help you find it!
                </Button>
              </>
            )}
            {showForm && (
              <NPIForm
                goBack={handleGoBack}
                fetchNpiList={(query: string) => void dispatch(fetchNPIList({ query }))}
                userList={npiUserList}
                onNpiSelect={handleSubmit}
              />
            )}

            <div className={styles.dottedLine}></div>

            <div className={styles.npiDialogActionsContainer}>
              {!showForm && (
                <Button
                  onClick={handleFetchNpiViaProfession}
                  variant="text"
                  className={styles.npiDialogActionButtons}
                  data-testid="dont-have-npi-button"
                >
                  I dont have an NPI
                </Button>
              )}
              <Button onClick={handleCloseNpi} variant="text" className={styles.npiDialogActionButtons}>
                Remind me next week
              </Button>
            </div>
          </>
        ) : (
          <>
            <Typography className={styles.npiDialogTitle} style={{ width: '100%', margin: 0 }}>
              Please select your profession:
            </Typography>
            <Button
              variant="text"
              onClick={() => setHasNpi(true)}
              className={styles.npiBackButton}
              style={{ alignSelf: 'flex-start' }}
              startIcon={<ArrowBackIosIcon fontSize="small" />}
              data-testid="back-button"
            >
              Back
            </Button>
            <NPIRadioButtons onProfessionSelect={(value) => handleSubmit(value, 'profession')} />
          </>
        )}
      </div>
    </Dialog>
  );
};

export default NPI;
